import { isEqual } from 'lodash'
import {
  IUserGroupsPreferences,
  ISavePreferenceRequestData,
  ISavePreferenceResponseData,
  ITransformedPreferenceResponseData,
  IGetPreferenceResponseData,
} from '../types/userPreferences'
import { ISelectInput, FormObjectType } from '../types/pack'
import { IOption } from '../types/common'
import { attemptJSONParse } from './common'
import {
  sphinxPreferencesHostUrl,
  sphinxSafeFetch,
  sphinxSafePost,
  sphinxSafeDelete,
  sphinxSafePut,
} from './http'
import { API, VALIDATION_PATTERNS } from '../config'

const sphinxPreferenceUrl = `${sphinxPreferencesHostUrl}/api/userpreference/${API.SPHINX_APP_ID}`

export const saveUserPreference = (
  data: ISavePreferenceRequestData
): Promise<ISavePreferenceResponseData> =>
  sphinxSafePost(sphinxPreferenceUrl, data)

export const getUserPreferences = (): Promise<ISavePreferenceResponseData[]> =>
  sphinxSafeFetch(sphinxPreferenceUrl)

export const deleteUserPreference = (prefKey: string): Promise<void> =>
  sphinxSafeDelete(`${sphinxPreferenceUrl}/${prefKey}`)

export const updateUserPreference = ({
  preferenceKey,
  preferenceValue,
}: ISavePreferenceRequestData): Promise<IGetPreferenceResponseData> =>
  sphinxSafePut(`${sphinxPreferenceUrl}/${preferenceKey}`, { preferenceValue })

export const generateUserPreferenceKey = (
  groupId: string,
  fieldName: string
): string => `${groupId}-${fieldName}`

export const transformStateToUserPreference = (
  groupId: string,
  fieldName: string,
  fieldValue: any
): ISavePreferenceRequestData => ({
  preferenceKey: generateUserPreferenceKey(groupId, fieldName),
  preferenceValue:
    typeof fieldValue === 'object' ? JSON.stringify(fieldValue) : fieldValue,
})

export const transformUserPreferenceToState = (
  preferenceResponseData: ISavePreferenceResponseData
): ITransformedPreferenceResponseData => ({
  fieldName: preferenceResponseData.preferenceKey.split('-')[1],
  fieldValue: attemptJSONParse(preferenceResponseData.preferenceValue),
})

export const transformUserPreferencesToState = (
  preferencesResponseData: IGetPreferenceResponseData[]
): IUserGroupsPreferences =>
  preferencesResponseData
    .filter(({ preferenceKey }) =>
      VALIDATION_PATTERNS.USER_PREFERENCE_KEY.test(preferenceKey)
    )
    .reduce((adaptedData, preference) => {
      const groupId = preference.preferenceKey.split('-')[0]
      const adaptedPreferenceResponseData = transformUserPreferenceToState(
        preference
      )
      return {
        ...adaptedData,
        [groupId]: {
          ...adaptedData[groupId],
          [adaptedPreferenceResponseData.fieldName]:
            adaptedPreferenceResponseData.fieldValue,
        },
      }
    }, {})

export const mapOptionsToStrings = (
  options: Array<ISelectInput | FormObjectType | IOption> = []
): string[] => options.map(({ value }) => value)

export const checkDefaultAndFieldValuesEquality = (
  fieldName: string,
  fieldValue: any,
  defaultValue: any
): boolean => {
  switch (fieldName) {
    // Contacts, tags and topicCodes objects may have unnecessary fields,
    // that would make them not equal, even though values are, so we map them to string values only.
    // Also since the order doesn't matter, we transform Arrays to Sets
    case 'contacts':
    case 'topicCodes':
    case 'tags':
      return isEqual(
        new Set(mapOptionsToStrings(fieldValue)),
        new Set(mapOptionsToStrings(defaultValue))
      )
    case 'location':
      return isEqual(fieldValue, defaultValue)
    default:
      return fieldValue === defaultValue
  }
}
